import { DEFAULT_VIEW, EXPLORER_URL, LAYERCAKE_METADATA } from "./config.ts";
import { areaOf, geometryOf } from "./geometry.ts";
import type { MapView, View } from "./map.ts";
import { getElement, type OsmElement, type OsmType, type Ref } from "./osm.ts";

export interface Detail {
  ref: Ref;
  loading: boolean;
  error: Error | null;
  element: OsmElement | null;
  area: number | null;
}

export class Store {
  detail: Detail | null = null;
  updatedAt: Date | null = null;
  map: MapView | null = null;

  // idempotency counter for request promises
  private request = 0;
  refresh: () => void;

  constructor(refresh: () => void) {
    this.refresh = refresh;
    this.load(readSelected());
    this.loadUpdatedAt();
  }

  get selected(): Ref | null {
    return this.detail?.ref ?? null;
  }

  get interactive(): boolean {
    return this.map?.isInteractive() ?? false;
  }

  get view(): View {
    return this.map?.view() ?? DEFAULT_VIEW;
  }

  setMap(map: MapView): void {
    this.map = map;
    this.refresh();
  }

  explorerUrl(): string {
    const { zoom, lat, lng } = this.view;
    return `${EXPLORER_URL}?layer=wetlands#${zoom.toFixed(2)}/${lat.toFixed(4)}/${lng.toFixed(4)}`;
  }

  select(ref: Ref | null): void {
    writeSelected(ref);
    this.map?.setSelected(ref);
    this.load(ref);
    this.refresh();
  }

  deselect(): void {
    this.select(null);
  }

  private load(ref: Ref | null): void {
    const request = ++this.request;
    if (!ref) {
      this.detail = null;
      return;
    }

    this.detail = {
      ref,
      loading: true,
      error: null,
      element: null,
      area: null,
    };
    this.loadElement(ref, request);
  }

  private current(request: number): Detail | null {
    return request === this.request ? this.detail : null;
  }

  private async loadElement(ref: Ref, request: number): Promise<void> {
    try {
      const full = await getElement(ref.type, ref.id);
      const detail = this.current(request);
      if (!detail) return;

      const geometry = geometryOf(full);
      detail.element = full.element;
      detail.area = geometry ? areaOf(geometry) : null;
      detail.loading = false;
      this.refresh();
    } catch (error) {
      const detail = this.current(request);
      if (!detail) return;
      detail.loading = false;
      detail.error = error instanceof Error ? error : new Error(String(error));
      this.refresh();
    }
  }

  private async loadUpdatedAt(): Promise<void> {
    const response = await fetch(LAYERCAKE_METADATA);
    if (!response.ok) return;
    const { timestamp } = (await response.json()) as { timestamp?: string };
    if (!timestamp) return;
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return;
    this.updatedAt = date;
    this.refresh();
  }
}

// The URL hash holds two parameters: `map=zoom/lat/lon`, which MapLibre's own
// hash control reads and writes, and `selected=type/id`, which is ours.

const TYPES: OsmType[] = ["node", "way", "relation"];

function readSelected(): Ref | null {
  const value = new URLSearchParams(location.hash.replace(/^#/, "")).get("selected");
  if (!value) return null;
  const [type, id] = value.split("/");
  if (!type || !id || !TYPES.includes(type as OsmType)) return null;
  const parsed = Number.parseInt(id, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return { type: type as OsmType, id: parsed };
}

function writeSelected(ref: Ref | null): void {
  const params = new URLSearchParams(location.hash.replace(/^#/, ""));
  if (ref) {
    params.set("selected", `${ref.type}/${ref.id}`);
  } else {
    params.delete("selected");
  }
  const query = params.toString();
  const hash = query ? `#${decodeURIComponent(query)}` : "";
  if (hash === location.hash) return;
  history.replaceState(null, "", hash || location.pathname + location.search);
}
